import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { postReferralTriggerEvent, putReferralTriggerEvent } from "store/referral-trigger-event/thunk";

export interface Props {
  id?: string;
  item?: any;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  lstLevel?: any;
  onCloseClick?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const RuleForm = ({
  id = "",
  item,
  triggerRefresh,
  isShowDetail = false,
  lstLevel,
  onCloseClick
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {
        code: values?.code,
        name: values?.name,
        valid_within_hours: values?.valid_within_hours,
        status: values?.status == 1 ? 1 : 0
      };

      const response: any = id
        ? await putReferralTriggerEvent(id, data)
        : await postReferralTriggerEvent(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    code: Yup.string().required(`${t("Code is required")}`),
    name: Yup.string().required(`${t("Name is required")}`),
    valid_within_hours: Yup.number().required(`${t("Valid hours is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      code: "",
      name: "",
      valid_within_hours: 24,
      status: 1
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseToggle = () => {
    onCloseClick && onCloseClick();
  };

  useEffect(() => {
    if(item) {
      formik.setValues({...item});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Button Create Reward Distribution")} - ${t("Referral Rules")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="l">
        <ModalHeader toggle={isLoading ? () => {} : handleCloseToggle}>
          {id ? t("Button Update Reward Distribution") : t("Button Create Reward Distribution")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 3) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="code" className="form-label">
                        {t("Code")} <span className="text-danger"> *</span>
                      </Label>

                      <Input
                        name="code"
                        type="text"
                        className="form-control"
                        id="code"
                        placeholder={t("Code")}
                        value={formik?.values?.code}
                        onChange={(event: any) =>
                          formik.setFieldValue("code", event?.target?.value)
                        }
                      />

                      {formik.touched.code && formik.errors.code ? (
                        <div className="text-danger mt-2">
                          {formik.errors.code}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Name")} <span className="text-danger"> *</span>
                      </Label>

                      <Input
                        name="name"
                        type="text"
                        className="form-control"
                        id="name"
                        placeholder={t("Name")}
                        value={formik?.values?.name}
                        onChange={(event: any) =>
                          formik.setFieldValue("name", event?.target?.value)
                        }
                      />

                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="valid_within_hours" className="form-label">
                        {t("Valid Within Hours")} <span className="text-danger"> *</span>
                      </Label>

                      <Input
                        name="valid_within_hours"
                        type="number"
                        className="form-control"
                        id="valid_within_hours"
                        placeholder={t("Enter name")}
                        value={formik?.values?.valid_within_hours || 0}
                        onChange={(event: any) =>
                          formik.setFieldValue("valid_within_hours", event?.target?.value)
                        }
                      />

                      {formik.touched.valid_within_hours && formik.errors.valid_within_hours ? (
                        <div className="text-danger mt-2">
                          {formik.errors.valid_within_hours}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            checked={formik?.values?.status == 1 ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue(
                                "status",
                                event?.target?.checked
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status && formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.status} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update")
                          : t("Button Create")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default RuleForm;
